/**
 * <AppearanceSection> — « Apparence » : Automatique, Clair ou Sombre (privé à la feuille « Changer de
 * profil »).
 *
 * Deux appelants, `<ProfileSwitcherSheet>` et la feuille « ⋮ » du live (`<LiveMoreSheet>`). Trois pilules de sélection (`<ChoiceChip
 * variant="pill">`) : c'est un choix, pas une action — elles se lisent au remplissage dans les deux
 * thèmes et se disent `radio` aux lecteurs d'écran. La rangée passe à la ligne quand la feuille est
 * trop étroite. Aucune ligne d'explication sous Automatique (Wajih, 30/09) : elle prenait trop de
 * place pour un mot qui se comprend seul.
 *
 * Contrôlée : `value` arrive, `onChange` repart. Le composant ne sait ni où la valeur est gardée ni
 * ce qu'elle change — c'est l'affaire de l'appelant, défaut compris : Automatique chez le parent,
 * Sombre chez l'élève (Wajih, 15/10).
 */
import React from "react"
import { View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { ChoiceChip } from "../../atoms/ChoiceChip"
import { Stack } from "../../atoms/containers/Stack"
import { SectionLabel } from "../../atoms/typography/SectionLabel"

/** L'apparence voulue : `auto` suit le réglage de l'appareil. */
export type AppearanceChoice = "auto" | "light" | "dark"

export type AppearanceSectionProps = {
    value: AppearanceChoice
    onChange: (value: AppearanceChoice) => void
    testID?: string
}

const OPTIONS: { value: AppearanceChoice; label: string }[] = [
    { value: "auto", label: "Automatique" },
    { value: "light", label: "Clair" },
    { value: "dark", label: "Sombre" },
]

export function AppearanceSection(props: AppearanceSectionProps) {
    const { value, onChange, testID } = props

    return (
        <Stack gap="2" style={styles.section} testID={testID}>
            <SectionLabel>Apparence</SectionLabel>
            <View accessibilityRole="radiogroup" accessibilityLabel="Apparence" style={styles.choices}>
                {OPTIONS.map((option) => (
                    <ChoiceChip
                        key={option.value}
                        variant="pill"
                        label={option.label}
                        selected={value === option.value}
                        onPress={() => onChange(option.value)}
                        testID={testID != null ? `${testID}-${option.value}` : undefined}
                    />
                ))}
            </View>
        </Stack>
    )
}

const styles = StyleSheet.create((theme) => ({
    // Le bord gauche des lignes de profil (leur `padding` de 3), pour que « Apparence » s'aligne sur
    // les avatars ; l'écart du dessus sépare la section de la liste sans filet.
    section: {
        paddingHorizontal: theme.spacing["3"],
        paddingTop: theme.spacing["4"],
    },
    choices: {
        flexDirection: "row",
        flexWrap: "wrap",
        gap: theme.spacing["2"],
    },
}))
